import catalog from "./catalog.json";
import type { SkillResult } from "../../../../packages/question-presentation/result-analytics";

export type CurriculumSection = "math" | "reading" | "writing";
export type Lesson = (typeof catalog)[number];
export const lessons = catalog;
export const sectionNames: Record<CurriculumSection, string> = { math: "Math", reading: "Reading", writing: "Writing" };
export const slideKey = (lesson: Lesson) => `curriculum/slides/${lesson.sha256}.pdf`;
export type Completion = { lessonId: string; completedAt: number };
export type DomainTotal = { domain: string; correct: number; scored: number };
export type CurriculumPlan = { studentId: string; loadedPoints: number; skillTotals: SkillResult[]; domainTotals: DomainTotal[]; completed: Completion[] };

type Alignment = { domains: string[]; skills?: RegExp; requires?: number[] };
const algebra = "Algebra", advanced = "Advanced Math", data = "Problem-Solving and Data Analysis", geometry = "Geometry and Trigonometry";
const craft = "Craft and Structure", information = "Information and Ideas", conventions = "Standard English Conventions", expression = "Expression of Ideas";
// Broad official skills cover several decks; they cannot identify an unrecorded subskill.
const alignment: Record<CurriculumSection, Alignment[]> = {
  math: [
    { domains: [] },
    { domains: [advanced], skills: /exponent|radical/ },
    { domains: [advanced], skills: /expression|factor/ },
    { domains: [algebra], skills: /\blinear equation|solv.*equation|rearrang|equivalent equation/, requires: [2] },
    { domains: [algebra, advanced], skills: /equation|nonlinear system/, requires: [3] },
    { domains: [algebra, advanced], skills: /function/, requires: [3] },
    { domains: [algebra], skills: /\blinear function|\blinear relationship|\blinear equation.*two|slope|parallel line/, requires: [3] },
    { domains: [algebra], skills: /interpret.*linear|\blinear model|\blinear equation.*context|\blinear function/, requires: [6] },
    { domains: [data], skills: /percent/ },
    { domains: [advanced, data], skills: /exponential|\blinear growth|nonlinear function/, requires: [1, 5] },
    { domains: [data], skills: /ratio|rate|proportion|unit|conversion/ },
    { domains: [algebra], skills: /word problem|mixture|\blinear equation.*context/, requires: [3] },
    { domains: [advanced], skills: /quadratic|polynomial|nonlinear equation/, requires: [1, 2, 3] },
    { domains: [algebra, advanced], skills: /system/, requires: [3] },
    { domains: [algebra], skills: /inequalit/, requires: [3] },
    { domains: [advanced], skills: /nonlinear function|transform|function parameter/, requires: [5] },
    { domains: [advanced], skills: /quadratic|nonlinear function|vertex/, requires: [12, 15] },
    { domains: [geometry], skills: /angle|lines, angles, and triangles|parallel line/ },
    { domains: [geometry], skills: /triangle|similar/, requires: [17] },
    { domains: [geometry], skills: /circle/ },
    { domains: [geometry], skills: /radian|degree|unit circle/, requires: [19] },
    { domains: [geometry], skills: /trigonom|right triangle/, requires: [18] },
    { domains: [geometry], skills: /area|perimeter|volume|surface/ },
    { domains: [data], skills: /data|table|scatterplot|distribution/ },
    { domains: [data], skills: /probability/ },
    { domains: [data], skills: /one.variable data|distribution|mean|median|center|spread|deviation|statistic/, requires: [23] },
    { domains: [data], skills: /sample|margin|statistical claim|stud|experiment|two.variable data|scatterplot|model/, requires: [23, 25] }
  ],
  reading: [
    { domains: [] },
    { domains: [craft], skills: /words in context/ },
    { domains: [craft], skills: /words in context/, requires: [2] },
    { domains: [craft, information], skills: /reference|cohesion/ },
    { domains: [information], skills: /central idea|main idea/ },
    { domains: [craft], skills: /text structure|text.*purpose/ },
    { domains: [information], skills: /central idea.*detail|detail|paraphrase/, requires: [5] },
    { domains: [craft], skills: /text structure|function in context/, requires: [6] },
    { domains: [craft], skills: /main purpose|text structure.*purpose/, requires: [6] },
    { domains: [information], skills: /inference/ },
    { domains: [information], skills: /inference/, requires: [10] },
    { domains: [information], skills: /command of evidence(?!.*quantitative)|textual evidence/ },
    { domains: [information], skills: /command of evidence(?!.*quantitative)|textual evidence/, requires: [12] },
    { domains: [information], skills: /quantitative/ },
    { domains: [information], skills: /quantitative/, requires: [14] },
    { domains: [craft], skills: /cross.text/ },
    { domains: [craft], skills: /cross.text/, requires: [16] },
    { domains: [], requires: Array.from({ length: 17 }, (_, i) => i + 1) }
  ],
  writing: [
    { domains: [] },
    { domains: [expression], skills: /transition/ },
    { domains: [expression], skills: /transition/, requires: [2] },
    { domains: [expression], skills: /synthesis/ },
    { domains: [expression], skills: /synthesis/, requires: [4] },
    { domains: [conventions], skills: /boundar|finiteness|sentence architecture/ },
    { domains: [conventions], skills: /boundar|finiteness|sentence architecture/, requires: [6] },
    { domains: [conventions], skills: /boundar/, requires: [7] },
    { domains: [conventions], skills: /boundar/, requires: [8] },
    { domains: [conventions], skills: /boundar|essential|comma/, requires: [7] },
    { domains: [conventions], skills: /boundar|essential|comma/, requires: [10] },
    { domains: [conventions], skills: /form, structure|subject.verb|verb agreement/, requires: [7] },
    { domains: [conventions], skills: /form, structure|verb tense|verb.*form|finiteness/, requires: [7] },
    { domains: [conventions], skills: /form, structure|pronoun/, requires: [7] },
    { domains: [conventions], skills: /form, structure|genitive|plural|possessive|apostrophe/ },
    { domains: [conventions], skills: /form, structure|modifi/, requires: [7] },
    { domains: [conventions], skills: /form, structure|coordination|parallel/, requires: [7] },
    { domains: [], requires: Array.from({ length: 17 }, (_, i) => i + 1) }
  ]
};

function normalized(text: string) { return text.toLowerCase().replace(/[–—-]/g, " ").replace(/\s+/g, " ").trim(); }
export function lessonAlignment(lesson: Lesson) {
  const section = lesson.section as CurriculumSection;
  return alignment[section][lesson.order - (section === "math" ? 0 : 1)];
}

export function recommendLessons(section: CurriculumSection, plan: Pick<CurriculumPlan, "skillTotals" | "domainTotals" | "completed">) {
  const completed = new Map(plan.completed.map(item => [item.lessonId, item.completedAt]));
  const course = lessons.filter(lesson => lesson.section === section);
  const rows = course.map(lesson => {
    const target = lessonAlignment(lesson);
    const skills = plan.skillTotals.filter(item => item.scored > 0 && item.section === (section === "math" ? "math" : "reading_writing") && target.skills?.test(normalized(item.skill)));
    const domains = plan.domainTotals.filter(item => item.scored > 0 && target.domains.some(domain => normalized(domain) === normalized(item.domain)));
    const evidence = skills.length ? skills : domains;
    const correct = evidence.reduce((sum, item) => sum + item.correct, 0), scored = evidence.reduce((sum, item) => sum + item.scored, 0);
    const basis = skills.length ? "skill" : scored ? "domain" : "sequence";
    const priority = scored ? (scored - correct) / (scored + 4) * (basis === "domain" ? 0.5 : 1) : 0;
    const reason = scored ? `${correct}/${scored} correct (${Math.round(correct * 100 / scored)}%) · ${basis === "skill" ? skills.map(item => item.skill).join(", ") : "Broad domain evidence: " + domains.map(item => item.domain).join(", ")}${scored < 3 ? " · Limited evidence" : ""}` : "Course order · No matching exam evidence yet";
    return { ...lesson, completedAt: completed.get(lesson.id) ?? null, reason, priority, basis, scored, correct, prerequisiteFor: null as string | null };
  });
  const pending: typeof rows = [], emitted = new Set<string>();
  function append(row: (typeof rows)[number], prerequisiteFor: string | null = null) {
    if (row.completedAt !== null || emitted.has(row.id)) return;
    emitted.add(row.id);
    const required = new Set([course[0].order, ...(lessonAlignment(row).requires ?? [])]);
    for (const prior of rows.filter(item => item.id !== row.id && required.has(item.order))) append(prior, row.title);
    pending.push({ ...row, prerequisiteFor });
  }
  for (const row of rows.filter(item => item.completedAt === null).sort((a, b) => b.priority - a.priority || a.order - b.order)) append(row);
  return { pending, completed: rows.filter(item => item.completedAt !== null) };
}
